/**
 * 알림 처리 헬퍼 함수들
 * Header 컴포넌트에서 사용하는 알림 액션 핸들러
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import { updateNotificationStatus } from '@/apis/notifications/notification.service'
import type { NotificationMessage, NotificationAction } from '@/types/notification'

interface TeamMemberBeforeUpdate {
  invite_notification_id?: number | null
}

interface NotificationPayload {
  teamId?: number
}

interface NotificationData {
  sender_profile_id?: string
  payload?: NotificationPayload
}

interface TeamMemberData {
  id: number
  profile_id?: string
  team_id?: number
  teams?: {
    manager_profile_id?: string
  }
}

/**
 * payload 정규화 (문자열이면 JSON.parse)
 */
function normalizePayload(payload: unknown): any {
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload)
    } catch (e) {
      console.warn('[notification-handlers] payload 파싱 실패:', e, '원본:', payload)
      return {}
    }
  }
  return payload ?? {}
}

/**
 * 팀 초대 처리 (team_member_requests 기반)
 */
export const handleTeamInviteAction = async (
  supabase: SupabaseClient,
  msg: NotificationMessage,
  action: NotificationAction
): Promise<void> => {
  // 디버깅: payload 확인
  const rawPayload = (msg as any).payload || {}
  const payload = normalizePayload(rawPayload)
  console.log('[팀 초대 핸들러] 알림 타입:', msg.type, 'rawPayload typeof:', typeof rawPayload, 'payload:', payload)

  // notification payload에서 requestId 가져오기 (여러 키 이름 지원)
  const requestId =
    payload?.requestId ??
    payload?.request_id ??
    payload?.teamMemberRequestId ??
    payload?.team_member_request_id ??
    (msg as any).proposal_id // 레거시 호환

  if (!requestId) {
    console.error('[팀 초대 핸들러] requestId를 찾을 수 없음:', {
      messageType: msg.type,
      payload,
      proposal_id: (msg as any).proposal_id,
    })

    // UX 폴백: 팀 페이지로 이동 안내
    if (typeof window !== 'undefined') {
      const error = new Error(
        '초대 정보를 다시 불러올게요. 팀 페이지에서 처리해주세요.'
      ) as Error & { redirectTo?: string }
      error.redirectTo = '/my/freelancer/teams'
      throw error
    }

    throw new Error('requestId가 없어 팀 초대를 처리할 수 없습니다.')
  }

  // 새 API route 호출
  try {
    const response = await fetch(`/api/team/member-requests/${requestId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: action === 'accept' ? 'accept' : 'decline',
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || '팀 초대 처리에 실패했습니다.')
    }

    // 액션 성공 후 알림 읽음 처리 (notification_id가 있는 경우)
    if (msg.notification_id) {
      try {
        const nextStatus = action === 'accept' ? 'ACCEPTED' : 'DECLINED'
        await updateNotificationStatus(msg.notification_id, nextStatus)
      } catch (notifError) {
        // 알림 업데이트 실패는 치명적이지 않음 (로깅만)
        console.warn('[팀 초대 핸들러] 알림 상태 업데이트 실패 (치명적이지 않음):', notifError)
      }
    }
  } catch (error: any) {
    console.error('팀 초대 처리 실패:', error)
    throw error
  }
}

/**
 * 팀 제안 처리 (team_member_requests 기반)
 */
export const handleTeamProposalAction = async (
  msg: NotificationMessage,
  action: NotificationAction
): Promise<void> => {
  // 디버깅: payload 확인
  const rawPayload = (msg as any).payload || {}
  const payload = normalizePayload(rawPayload)
  console.log('[팀 제안 핸들러] 알림 타입:', msg.type, 'rawPayload typeof:', typeof rawPayload, 'payload:', payload)

  // notification payload에서 requestId 가져오기 (여러 키 이름 지원)
  const requestId =
    payload?.requestId ??
    payload?.request_id ??
    payload?.teamMemberRequestId ??
    payload?.team_member_request_id ??
    msg.proposal_id // 레거시 호환

  if (!requestId) {
    console.error('[팀 제안 핸들러] requestId를 찾을 수 없음:', {
      messageType: msg.type,
      payload,
      proposal_id: msg.proposal_id,
    })

    // UX 폴백: 팀 페이지로 이동 안내
    if (typeof window !== 'undefined') {
      const error = new Error(
        '제안 정보를 다시 불러올게요. 팀 페이지에서 처리해주세요.'
      ) as Error & { redirectTo?: string }
      error.redirectTo = '/my/freelancer/teams'
      throw error
    }

    throw new Error('requestId가 없어 팀 제안을 처리할 수 없습니다.')
  }

  // 새 API route 호출
  try {
    const response = await fetch(`/api/team/member-requests/${requestId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: action === 'accept' ? 'accept' : 'decline',
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || '팀 제안 처리에 실패했습니다.')
    }

    // 액션 성공 후 알림 읽음 처리 (notification_id가 있는 경우)
    if (msg.notification_id) {
      try {
        const nextStatus = action === 'accept' ? 'ACCEPTED' : 'DECLINED'
        await updateNotificationStatus(msg.notification_id, nextStatus)
      } catch (notifError) {
        // 알림 업데이트 실패는 치명적이지 않음 (로깅만)
        console.warn('[팀 제안 핸들러] 알림 상태 업데이트 실패 (치명적이지 않음):', notifError)
      }
    }
  } catch (error: any) {
    console.error('팀 제안 처리 실패:', error)
    throw error
  }
}

/**
 * 견적 알림 처리
 */
export const handleEstimateNotificationAction = async (
  msg: NotificationMessage,
  action: NotificationAction
): Promise<void> => {
  // ESTIMATE_REQUEST 알림의 경우 respondEstimateRequest API 사용
  if (msg.type === 'estimate_request') {
    const payload = normalizePayload((msg as any).payload || {})
    const requestId = payload.requestId ?? payload.request_id ?? msg.proposal_id
    
    if (!requestId) {
      throw new Error('견적 요청 ID가 없어 처리할 수 없습니다.')
    }

    try {
      const { respondEstimateRequest } = await import('@/apis/estimates/estimate-requests.service')
      await respondEstimateRequest({
        requestId: Number(requestId),
        action: action === 'accept' ? 'ACCEPT' : 'DECLINE',
      })
      // respondEstimateRequest 내부에서 알림 상태도 업데이트하므로 여기서는 추가 작업 불필요
    } catch (error: any) {
      console.error('견적 요청 응답 실패:', error)
      throw error
    }
    return
  }

  if (!msg.notification_id) {
    throw new Error('알림 ID가 없어 견적 알림을 처리할 수 없습니다.')
  }

  // ESTIMATE_SUBMITTED 알림의 경우 decideEstimate API 사용
  if (msg.type === 'estimate_submitted') {
    const estimateId = ((msg as any).payload as any)?.estimateId
    if (!estimateId) {
      throw new Error('견적서 ID가 없어 처리할 수 없습니다.')
    }

    try {
      const { decideEstimate } = await import('@/apis/estimates')
      await decideEstimate({
        estimateId,
        decision: action === 'accept' ? 'ACCEPT' : 'REJECT',
      })
      // 알림 상태도 업데이트
      const nextStatus = action === 'accept' ? 'ACCEPTED' : 'DECLINED'
      await updateNotificationStatus(msg.notification_id, nextStatus)
    } catch (error: any) {
      console.error('견적서 결정 실패:', error)
      throw error
    }
    return
  }

  // 기타 견적 관련 알림은 알림 상태만 업데이트
  const nextStatus = action === 'accept' ? 'ACCEPTED' : 'DECLINED'
  await updateNotificationStatus(msg.notification_id, nextStatus)
}

/**
 * 팀 합류 요청 처리 (team_member_requests 기반)
 */
export const handleTeamJoinRequestAction = async (
  supabase: SupabaseClient,
  msg: NotificationMessage,
  action: NotificationAction
): Promise<void> => {
  // 디버깅: payload 확인
  const rawPayload = (msg as any).payload || {}
  const payload = normalizePayload(rawPayload)
  console.log('[팀 합류 신청 핸들러] 알림 타입:', msg.type, 'rawPayload typeof:', typeof rawPayload, 'payload:', payload)

  // notification payload에서 requestId 가져오기 (여러 키 이름 지원)
  const requestId =
    payload?.requestId ??
    payload?.request_id ??
    payload?.teamMemberRequestId ??
    payload?.team_member_request_id

  if (!requestId) {
    console.error('[팀 합류 신청 핸들러] requestId를 찾을 수 없음:', {
      messageType: msg.type,
      payload,
    })

    // UX 폴백: 팀 설정 탭으로 이동 안내
    if (typeof window !== 'undefined') {
      const error = new Error(
        '합류 신청 정보를 다시 불러올게요. "팀 설정" 탭에서 처리해주세요.'
      ) as Error & { redirectTo?: string }
      error.redirectTo = '/my/freelancer/teams?tab=settings'
      throw error
    }

    throw new Error('requestId가 없어 팀 합류 신청을 처리할 수 없습니다.')
  }

  // 새 API route 호출
  try {
    const response = await fetch(`/api/team/member-requests/${requestId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: action === 'accept' ? 'accept' : 'decline',
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || '팀 합류 신청 처리에 실패했습니다.')
    }

    // 액션 성공 후 알림 읽음 처리 (notification_id가 있는 경우)
    if (msg.notification_id) {
      try {
        const nextStatus = action === 'accept' ? 'ACCEPTED' : 'DECLINED'
        await updateNotificationStatus(msg.notification_id, nextStatus)
      } catch (notifError) {
        // 알림 업데이트 실패는 치명적이지 않음 (로깅만)
        console.warn('[팀 합류 신청 핸들러] 알림 상태 업데이트 실패 (치명적이지 않음):', notifError)
      }
    }
  } catch (error: any) {
    console.error('팀 합류 신청 처리 실패:', error)
    throw error
  }
}

